"use client";

import { useCallback } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { SearchFiltersSchemaMinDegree } from "@/lib/api/model";
import type { AutocompleteResult, VariantFilterValue } from "@/lib/api/types";
import { hasEntityForm } from "@/lib/search/entity-variants";
import { VariantsDropdown } from "@/features/search/components/filters/input/internal/variants-dropdown";
import {
  BulkModifyDropdown,
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
  FilterLabel,
  PresetButton,
} from "../shared";
import { FilterGraduationYearBetweenInput } from "@/features/search/components/filters/input/between-input";
import { FilterLocationInput } from "@/features/search/components/filters/input/location-input";
import { FilterSelectInput } from "@/features/search/components/filters/input/select-input";
import { FilterUniversityInput } from "@/features/search/components/filters/input/university-input";
import { parseLocationResult } from "@/features/search/lib/parse-location-result";
import type { PresetTypeSlug } from "@/features/presets";

type MinDegreeOption = SearchFiltersSchemaMinDegree | "none";

const MIN_DEGREE_OPTIONS: { value: MinDegreeOption; label: string }[] = [
  { value: "none", label: "No Minimum" },
  { value: "associates", label: "Associates" },
  { value: "bachelors", label: "Bachelors" },
  { value: "masters", label: "Masters" },
  { value: "doctorate", label: "Doctorate" },
  { value: "phd", label: "PhD" },
];

export function FilterEducationSection({ onOpenPreset }: { onOpenPreset?: (type: PresetTypeSlug) => void }) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const universities = filters.universities ?? [];
  const universityLocations = filters.university_locations ?? [];
  const majors = filters.majors ?? [];

  const updateUniversity = useCallback(
    (index: number, updated: VariantFilterValue) => {
      const next = [...universities];
      next[index] = updated;
      updateFilter("universities", next);
    },
    [universities, updateFilter],
  );

  const addUniversity = (university: VariantFilterValue) => {
    if (hasEntityForm(universities, university.text ?? "")) return;
    updateFilter("universities", [...universities, university]);
  };

  const addMajor = (text: string) => {
    if (hasEntityForm(majors, text)) return;
    updateFilter("majors", [...majors, { text, required: false }]);
  };

  const removeUniversity = useCallback(
    (index: number) => {
      updateFilter("universities", universities.filter((_, j) => j !== index));
    },
    [universities, updateFilter],
  );

  const bulkSetScope = useCallback(
    (scope: "c" | "p" | "cp") => {
      updateFilter("universities", universities.map((u) => ({ ...u, scope })));
    },
    [universities, updateFilter],
  );

  const bulkExclude = useCallback(() => {
    updateFilter("universities", universities.map((u) => ({ ...u, mode: "exclude" as const })));
  }, [universities, updateFilter]);

  const bulkUnexclude = useCallback(() => {
    updateFilter("universities", universities.map((u) => ({ ...u, mode: "include" as const })));
  }, [universities, updateFilter]);

  const bulkRequire = useCallback(() => {
    updateFilter("universities", universities.map((u) => ({ ...u, required: true })));
  }, [universities, updateFilter]);

  const bulkUnrequire = useCallback(() => {
    updateFilter("universities", universities.map((u) => ({ ...u, required: false })));
  }, [universities, updateFilter]);

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full">
          <FilterLabel>Universities</FilterLabel>
          {universities.length > 0 && (
            <BulkModifyDropdown
              onSetScope={bulkSetScope}
              onExcludeAll={bulkExclude}
              onUnexcludeAll={bulkUnexclude}
              onRequireAll={bulkRequire}
              onUnrequireAll={bulkUnrequire}
            />
          )}
        </div>
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="universities"
            placeholder="Search..."
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              addUniversity({
                canonical_id: result.canonical_id ?? undefined,
                text: result.value,
                domain: result.domain ?? undefined,
                scope: "cp",
                required: false,
              });
            }}
            onFreeText={(text: string) => {
              addUniversity({ text, scope: "cp", required: false });
            }}
          />
          <PresetButton label="University Presets" onClick={() => onOpenPreset?.("universities")} />
        </div>
      </div>

      {universities.map((uni, i) => (
        <FilterUniversityInput
          key={`${uni.text}-${i}`}
          university={uni}
          variantsField="universities"
          onChange={(updated) => updateUniversity(i, updated)}
          onRemove={() => removeUniversity(i)}
        />
      ))}

      {universities.length > 1 && (
        <div className="flex justify-end">
          <ClearChipsButton onClick={() => updateFilter("universities", undefined)} />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>University Locations</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="locations"
            placeholder="Search..."
            allowFreeText={false}
            onSelect={(result: AutocompleteResult) => {
              updateFilter("university_locations", [...universityLocations, parseLocationResult(result)]);
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {universityLocations.map((loc, i) => (
        <FilterLocationInput
          key={`uni-loc-${loc.display_name ?? loc.region ?? loc.country}-${i}`}
          location={loc}
          onChange={(updated) => {
            const next = [...universityLocations];
            next[i] = updated;
            updateFilter("university_locations", next);
          }}
          onRemove={() =>
            updateFilter("university_locations", universityLocations.filter((_, j) => j !== i))
          }
        />
      ))}

      <FilterFieldContainer>
        <FilterFieldLabel>Graduation Year</FilterFieldLabel>
        <FilterFieldContent>
          <FilterGraduationYearBetweenInput />
        </FilterFieldContent>
      </FilterFieldContainer>

      <FilterFieldContainer>
        <FilterFieldLabel>Majors</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="majors"
            placeholder="Search..."
            onSelect={(result: AutocompleteResult) => addMajor(result.value)}
            onFreeText={(text: string) => addMajor(text.toLowerCase())}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {majors.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {majors.map((major, i) => {
            const excluded = major.mode === "exclude";
            const required = major.required ?? false;
            return (
              <FilterChip
                key={i}
                label={major.text}
                variants={major.variants}
                beforeActions={
                  <VariantsDropdown
                    compact
                    field="majors"
                    item={major}
                    onChange={(updated) => {
                      const next = [...majors];
                      next[i] = updated;
                      updateFilter("majors", next);
                    }}
                  />
                }
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...majors];
                  next[i] = { ...major, mode: excluded ? "include" : "exclude" };
                  updateFilter("majors", next);
                }}
                onToggleRequired={() => {
                  const next = [...majors];
                  next[i] = { ...major, required: !required };
                  updateFilter("majors", next);
                }}
                onRemove={() => updateFilter("majors", majors.filter((_, j) => j !== i))}
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter("majors", undefined)} />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Minimum Degree</FilterFieldLabel>
        <FilterFieldContent>
          <FilterSelectInput<MinDegreeOption>
            options={MIN_DEGREE_OPTIONS}
            value={filters.min_degree ?? "none"}
            onChange={(v) => updateFilter("min_degree", v === "none" ? undefined : v)}
            placeholder="No Minimum"
          />
        </FilterFieldContent>
      </FilterFieldContainer>
    </>
  );
}
